import { create } from 'zustand';
import type { WeatherStation } from '../types/weatherStation';
import { createLogger } from '../utils/logger';
import { fetchJSON } from '../utils/fetchClient';
import { getStationTier } from '../utils/stationTier';

const log = createLogger('StationDataStore');

// Bounded auto-retry for the boot bundle. A single App Runner instance restart
// wipes the server's in-memory metadata cache, so the first cold request after a
// deploy/health-restart can fail or time out; without a retry the client stays
// permanently blind (empty allStations) until a full page reload. The ⌘K palette
// now searches server-side so it survives this regardless, but the map + the
// palette's offline fallback both read allStations, so we recover it in the
// background. Guarded so overlapping calls never stack retries.
const MAX_LOAD_RETRIES = 3;
const LOAD_RETRY_BASE_MS = 4000; // 4s, 8s, 16s (exponential)
// After the bounded retries are exhausted, fall back to a slow steady-state
// re-attempt interval rather than giving up entirely — so the app still recovers
// if the backend comes back, without ever hammering it.
const LOAD_FAILURE_COOLDOWN_MS = 60000;
let loadRetryCount = 0;
let loadRetryTimer: ReturnType<typeof setTimeout> | null = null;
// Backoff gate that stops loadAllStations from becoming a request storm when every
// fetch fails FAST (e.g. the backend rate-limits the cold boot: fetchJSON throws on
// the 429 in milliseconds). The entry guard below only checked allStations.length,
// which never populates while requests fail — so any caller whose effect re-runs on
// the isLoadingAllStations true→false toggle (StatisticsDashboard, StationDetail)
// re-invoked the loader in a tight loop, firing the /station-metadata →
// /weatherstations pair hundreds of times a minute. A failure arms loadCooldownUntil
// so those external re-invocations no-op until the backoff elapses; the sanctioned
// internal retry clears it when it's genuinely time to try again.
let loadCooldownUntil = 0;

export interface StationDataState {
  allStations: WeatherStation[];
  isLoadingAllStations: boolean;
  selectedStation: WeatherStation | null;
  selectedStations: WeatherStation[];
}

export interface StationDataActions {
  loadAllStations: () => Promise<void>;
  setSelectedStation: (station: WeatherStation | null) => void;
  setSelectedStations: (stations: WeatherStation[]) => void;
}

export type StationDataStore = StationDataState & StationDataActions;

export const useStationDataStore = create<StationDataStore>((set, get) => ({
  allStations: [],
  isLoadingAllStations: false,
  selectedStation: null,
  selectedStations: [],

  setSelectedStation: (station) => set({ selectedStation: station }),

  setSelectedStations: (stations) => set({ selectedStations: stations }),

  loadAllStations: async () => {
    if (get().allStations.length > 0) {
      log.debug('Stations already loaded, skipping...');
      return;
    }

    // In-flight dedupe: Zustand applies this set() synchronously, so isLoadingAllStations
    // is already true by the time a second caller (e.g. two components mounting in the
    // same tick, or an effect re-firing) re-enters — collapse those into the one request
    // already running instead of launching a parallel metadata→weatherstations pair.
    if (get().isLoadingAllStations) {
      log.debug('Station load already in flight, skipping...');
      return;
    }

    // Post-failure cooldown: after a failed load the backoff timer (or the steady-state
    // interval) owns the next attempt. External callers re-invoking in the meantime —
    // the effect-driven loop that produced the 429 storm — no-op until it elapses.
    const now = Date.now();
    if (loadCooldownUntil > now) {
      log.debug(`Station load cooling down for ${loadCooldownUntil - now}ms, skipping...`);
      return;
    }

    log.debug('Starting to load all stations...');
    set({ isLoadingAllStations: true });

    try {
      const baseURL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';

      let stationsData: any[] = [];
      let source = 'S3';

      try {
        // ponytail: fields=map drops unused metadata fields server-side, shrinking payload ~30-50%
        const metadataResponse = await fetchJSON<{ success?: boolean; metadata?: any }>(`${baseURL}/api/station-metadata?fields=map`, {
          signal: AbortSignal.timeout(60000),
        });

        if (!metadataResponse?.success || !metadataResponse?.metadata) {
          throw new Error('station-metadata returned invalid response');
        }

        const raw = metadataResponse.metadata;
        stationsData = raw.stations && Array.isArray(raw.stations)
          ? raw.stations
          : Array.isArray(raw) ? raw : [];
      } catch (metadataError) {
        // Backing store is S3 (one-obs-archived-prd) — R2 was fully retired, see lib/r2-storage.js.
        // Most common cause: cold App Runner instance hasn't warmed its in-memory cache yet, so the
        // S3 fetch + ~140MB JSON.parse blows past this 60s timeout. The server pre-warms this cache
        // on boot (server/index.js) to keep this path cold as rarely as possible.
        log.warn('⚠️ Station metadata endpoint unavailable, falling back to weather API:', metadataError instanceof Error ? metadataError.message : metadataError);
        source = 'weather API';
        // 120s (was 180s): the primary metadata endpoint already gets 60s; a 3-minute
        // fallback let a failing/blocked backend hang the app on a spinner for minutes.
        // 120s is a middle ground — trims the worst-case hang but keeps headroom for a
        // genuinely-slow cold-cache load of the large payload (a timeout can't tell
        // "backend down" from "slow but working", so don't starve the last-resort path).
        const fallbackData = await fetchJSON<any[]>(`${baseURL}/api/weather/weatherstations`, {
          signal: AbortSignal.timeout(120000),
        });
        if (!fallbackData || !Array.isArray(fallbackData)) {
          throw new Error('weatherstations returned non-array');
        }
        stationsData = fallbackData;
      }

      // Lightweight transform — handles both station-metadata (snake_case) and weather API (camelCase) formats
      const stations: WeatherStation[] = [];
      for (const s of stationsData) {
        const rawLat = s.geoPoint?.latitude ?? s.latitude;
        const rawLon = s.geoPoint?.longitude ?? s.longitude;
        const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat) || 0;
        const lon = typeof rawLon === 'number' ? rawLon : parseFloat(rawLon) || 0;
        if (lat === 0 && lon === 0) continue;
        if (Math.abs(lat) === 90) continue;

        const rawTags = s.tags;
        const tags = typeof rawTags === 'string' ? ((() => { try { return JSON.parse(rawTags); } catch { return {}; } })()) : (rawTags || {});

        // `active` is carried through from the metadata sync so the UI can label a
        // decommissioned network "offline" instead of hiding it. Default to true
        // (only an explicit false marks a device inactive).
        const mapDevice = (d: Record<string, unknown>) => ({
          stationId: String(d.station_id || d.stationId || ''),
          // The per-network identifier shown as "Station ID" in the Devices panel
          // (e.g. WMO 3772, GHCNh "UKI0000EGLL", ISD "037720-99999"). Distinct from
          // stationId, which mirrors the station code. Carried so Device ID search
          // can match it. Absent → leave undefined (not an empty string) so the
          // Devices panel's `sourceStationId || 'N/A'` fallback still reads right.
          sourceStationId: d.source_station_id || d.sourceStationId
            ? String(d.source_station_id || d.sourceStationId)
            : undefined,
          dataSourceId: String(d.data_source_id || d.dataSourceId || ''),
          type: String(d.network_type || d.type || ''),
          active: d.active !== false,
        });
        let devices: Array<{ stationId: string; sourceStationId?: string; dataSourceId: string; type: string; active?: boolean }> = [];
        const rawDevices = s.devices;
        if (typeof rawDevices === 'string') {
          try {
            const parsed = JSON.parse(rawDevices);
            if (Array.isArray(parsed)) devices = parsed.map(mapDevice);
          } catch { /* ignore malformed devices JSON */ }
        } else if (Array.isArray(rawDevices)) {
          devices = rawDevices.map(mapDevice);
        }

        stations.push({
          stationCode: s.station_code || s.stationCode || '',
          latitude: lat,
          longitude: lon,
          elevation: typeof s.elevation === 'number' ? s.elevation : parseFloat(s.elevation) || 0,
          country: s.country || '',
          state: s.state || '',
          city: s.city || '',
          stateType: s.stateType || s.state_type || '',
          cityType: s.cityType || s.city_type || '',
          countryCode: s.countryCode || s.country_code || '',
          stateCode: s.stateCode || s.state_code || '',
          cityCode: s.cityCode || s.city_code || '',
          admin: (() => {
            const raw = s.admin;
            const arr = typeof raw === 'string'
              ? (() => { try { return JSON.parse(raw); } catch { return null; } })()
              : raw;
            if (!Array.isArray(arr)) return undefined;
            return (arr as Array<Record<string, unknown>>)
              .filter((a) => a && typeof a.level === 'number' && !!a.name)
              .map((a) => ({
                level: a.level as number,
                name: String(a.name),
                type: String(a.type ?? ''),
                gid: a.gid ? String(a.gid) : undefined,
              }));
          })(),
          tags,
          firstObsTimestamp: s.first_obs_timestamp || s.firstObsTimestamp || '',
          lastObsTimestamp: s.last_obs_timestamp || s.lastObsTimestamp || '',
          startTime: s.start_time || s.startTime,
          devices,
          isActive: s.active === true || s.active === 'true',
          isPrimaryResult: true,
          stationTier: getStationTier(devices),
          parameters: (() => {
            const raw = s.parameters;
            if (Array.isArray(raw)) return raw.filter(Boolean) as string[];
            if (typeof raw === 'string') {
              try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed.filter(Boolean) : []; }
              catch { return []; }
            }
            return [];
          })(),
          // NB: per-parameter `coverage` is NOT loaded here — it's large and only
          // the station detail page needs it, so it's fetched lazily per station
          // via useStationCoverage (GET /api/station-metadata/:code/coverage).
        });
      }

      log.info(`✅ Successfully loaded ${stations.length} stations from ${source}`);
      loadRetryCount = 0; // healthy load resets the backoff
      loadCooldownUntil = 0;
      if (loadRetryTimer) { clearTimeout(loadRetryTimer); loadRetryTimer = null; }
      set({ allStations: stations, isLoadingAllStations: false });
    } catch (error) {
      log.error('❌ Failed to load all stations:', error);
      set({ isLoadingAllStations: false });

      // Background auto-retry with exponential backoff. allStations is still empty
      // (the mount-once guard keys on length), so a scheduled retry re-enters this
      // action cleanly. Bounded so a genuinely-down backend doesn't retry forever.
      // While a retry is pending, arm the cooldown to the SAME delay so external
      // callers (component effects re-firing on the isLoadingAllStations toggle)
      // can't jump the queue and fire their own request before the timer does.
      const delay = loadRetryCount < MAX_LOAD_RETRIES
        ? LOAD_RETRY_BASE_MS * Math.pow(2, loadRetryCount)
        : LOAD_FAILURE_COOLDOWN_MS;
      loadCooldownUntil = Date.now() + delay;
      if (!loadRetryTimer) {
        const isBounded = loadRetryCount < MAX_LOAD_RETRIES;
        if (isBounded) loadRetryCount++;
        log.warn(
          isBounded
            ? `Retrying station load in ${delay}ms (attempt ${loadRetryCount}/${MAX_LOAD_RETRIES})`
            : `Station load exhausted retries; next attempt in ${delay}ms`,
        );
        // The scheduled retry is the sanctioned next attempt: clear the cooldown just
        // before it runs so its own loadAllStations() call passes the gate. If it also
        // fails, this same block re-arms the cooldown (steady-state interval once the
        // bounded retries are spent) — recovering if the backend returns without ever
        // looping tightly.
        loadRetryTimer = setTimeout(() => {
          loadRetryTimer = null;
          loadCooldownUntil = 0;
          void get().loadAllStations();
        }, delay);
      }
    }
  },
}));
